// shadcn/ui component: https://ui.shadcn.com/docs/components/radix/dialog
import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import CloseIcon from '@mui/icons-material/Close';

import { cn } from 'src/component/ds/utils';
import { DsThemedPortal } from 'src/component/ds/DsRoot';

/*
 * "Dialog - Vega" in Figma. Radix portals to document.body, outside <DsRoot>,
 * so the portal re-applies DS_ROOT_CLASS to keep the reset and tokens.
 */

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'ds-fixed ds-inset-0 ds-z-50 ds-bg-overlay',
      'data-[state=open]:ds-animate-in data-[state=closed]:ds-animate-out',
      'data-[state=open]:ds-fade-in-0 data-[state=closed]:ds-fade-out-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DsThemedPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'ds-fixed ds-left-1/2 ds-top-1/2 ds-z-50 -ds-translate-x-1/2 -ds-translate-y-1/2',
          'ds-grid ds-w-full ds-max-w-[512px] ds-gap-4 ds-p-6',
          'ds-border ds-border-border ds-bg-background ds-text-foreground',
          'ds-rounded-ds ds-shadow-ds-lg',
          'data-[state=open]:ds-animate-in data-[state=closed]:ds-animate-out',
          'data-[state=open]:ds-fade-in-0 data-[state=closed]:ds-fade-out-0',
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className={cn(
            'ds-absolute ds-right-4 ds-top-4 ds-rounded-ds ds-opacity-70 ds-transition-opacity',
            'hover:ds-opacity-100 focus-visible:ds-outline-none focus-visible:ds-shadow-ds-focus',
            'disabled:ds-pointer-events-none'
          )}
        >
          <CloseIcon sx={{ fontSize: 16 }} aria-hidden='true' />
          <span className='ds-sr-only'>Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DsThemedPortal>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ds-flex ds-flex-col ds-gap-1.5', className)} {...props} />;
}

function DialogFooter ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('ds-flex ds-flex-col-reverse ds-gap-2 sm:ds-flex-row sm:ds-justify-end', className)}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('ds-text-ds-h4 ds-font-medium ds-text-foreground', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('ds-text-ds-sm ds-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription
};
